import { useState, useEffect, useCallback, useMemo } from 'react'
import { Drawer, IconButton, Switch, FormControlLabel, Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material'
import { default as SettingsIcon } from '@mui/icons-material/Settings'
import { default as CloseIcon } from '@mui/icons-material/Close'
import DragIndicator from '@mui/icons-material/DragIndicator'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import styled from 'styled-components'

const STORAGE_KEY = 'therapy-report-settings'

const DEFAULT_SECTIONS = [
  { id: 'patient-details', label: 'Patient Details', visible: false, order: 0 }, // Default to false, will be set based on device in getDefaultSections
  { id: 'therapy-history', label: 'Therapy History', visible: true, order: 1 },
  { id: 'consultation-history', label: 'Consultation History', visible: true, order: 2 },
  { id: 'type-of-therapy', label: 'Type of Therapy', visible: true, order: 3 },
  { id: 'additional-therapy', label: 'Additional Therapy Type', visible: true, order: 4 },
  { id: 'observations', label: 'Observations', visible: true, order: 5 },
  { id: 'complaints', label: 'Complaints', visible: true, order: 6 },
  { id: 'diagnosis', label: 'Diagnosis', visible: true, order: 7 },
  { id: 'primary-clinical-diagnosis', label: 'Primary Clinical Diagnosis', visible: true, order: 8 },
  { id: 'differential-clinical-diagnoses', label: 'Differential Clinical Diagnoses', visible: true, order: 9 },
  { id: 'advice', label: 'Advice', visible: true, order: 10 },
  { id: 'free-form-report', label: 'Therapy Report/Notes', visible: true, order: 11 },
  { id: 'documents-upload', label: 'Documents Upload', visible: true, order: 12 },
  { id: 'next-visit', label: 'Next Visit', visible: true, order: 13 },
  { id: 'private-notes', label: 'Private Notes', visible: true, order: 14 }, // Can be reordered by user
]

const DrawerContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: linear-gradient(135deg, rgba(250, 250, 255, 0.98) 0%, rgba(255, 255, 255, 0.98) 100%);
`

const DrawerHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: linear-gradient(135deg, rgba(67, 56, 202, 0.98) 0%, rgba(99, 102, 241, 0.98) 100%);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 4px 16px rgba(67, 56, 202, 0.25), 0 1px 3px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 10;
  flex-shrink: 0;
  min-height: 72px;
  
  @media (max-width: 768px) {
    padding: 12px 16px;
    min-height: 64px;
  }
`

const DrawerTitle = styled(Typography)`
  font-size: 20px;
  font-weight: 700;
  color: white;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  display: flex;
  align-items: center;
  gap: 10px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  letter-spacing: -0.02em;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 17px;
    gap: 8px;
  }
`

const DrawerContent = styled(Box)`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 16px 12px;
  }
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 12px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(67, 56, 202, 0.08);
    border-radius: 6px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(67, 56, 202, 0.3);
    border-radius: 6px;
    border: 2px solid rgba(67, 56, 202, 0.08);
    
    &:hover {
      background: rgba(67, 56, 202, 0.5);
    }
  }
  
  /* Firefox scrollbar */
  scrollbar-width: thin;
  scrollbar-color: rgba(67, 56, 202, 0.3) rgba(67, 56, 202, 0.08);
`

function SortableSectionItem({ section, isSupported, toggleVisibility }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: section.id,
    disabled: !isSupported,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition ? `${transition}, all 0.2s` : 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    p: 1.5,
    mb: 1,
    backgroundColor: isDragging ? '#f0f0f0' : isSupported ? 'white' : '#fafafa',
    border: `1px solid ${isSupported ? '#e0e0e0' : '#ff9800'}`,
    borderRadius: 1,
    opacity: isSupported ? 1 : 0.7,
  }

  return (
    <Box ref={setNodeRef} style={style}>
      {isSupported ? (
        <div {...attributes} {...listeners} style={{ cursor: 'grab' }}>
          <DragIndicator sx={{ color: '#999' }} />
        </div>
      ) : (
        <DragIndicator sx={{ color: '#ccc', cursor: 'not-allowed' }} />
      )}
      <Box flex={1}>
        <FormControlLabel
          control={
            <Switch
              checked={section.visible}
              onChange={() => toggleVisibility(section.id)}
              size="small"
              disabled={!isSupported}
            />
          }
          label={
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2">{section.label}</Typography>
              {!isSupported && (
                <Typography
                  variant="caption"
                  sx={{
                    color: '#ff9800',
                    fontWeight: 600,
                    fontSize: '10px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    backgroundColor: '#fff3e0',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    border: '1px solid #ffb74d',
                  }}
                >
                  Not Supported
                </Typography>
              )}
            </Box>
          }
          sx={{ margin: 0 }}
        />
      </Box>
      {isSupported && (
        section.visible ? (
          <Visibility sx={{ color: '#4caf50', fontSize: 20 }} />
        ) : (
          <VisibilityOff sx={{ color: '#999', fontSize: 20 }} />
        )
      )}
    </Box>
  )
}

const TherapyReportSettings = ({ onSettingsChange, onOpen }) => {
  const [open, setOpen] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  
  // Get default sections with device-aware visibility for patient-details
  const getDefaultSections = useCallback(() => {
    return DEFAULT_SECTIONS.map((section) => {
      if (section.id === 'patient-details') {
        return { ...section, visible: isMobile } // true on mobile, false on PC
      }
      return section
    })
  }, [isMobile])
  
  const [sections, setSections] = useState(() => getDefaultSections())
  
  // Expose open function to parent via onOpen callback
  useEffect(() => {
    if (onOpen) {
      onOpen(() => setOpen(true))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Load settings from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) {
        const parsed = JSON.parse(saved)
        
        // Remove any combined/legacy sections that have been split into individual sections
        const combinedSectionIds = [
          'complaints-observations-diagnosis',
          'complaints-observations-and-diagnosis',
          'complaints-observations-diagnosis-combined',
          'advice-notes', // Removed - split into 'advice' and 'free-form-report'
        ]
        const filteredParsed = parsed.filter(
          (section) => !combinedSectionIds.includes(section.id),
        )
        
        // Merge saved settings with defaults to ensure all sections are present
        const defaultSections = getDefaultSections()
        const mergedSections = defaultSections.map((defaultSection) => {
          const savedSection = filteredParsed.find((s) => s.id === defaultSection.id)
          if (savedSection) {
            // Use saved settings but preserve default order if order is missing
            // For patient-details, respect saved visibility if it exists
            return {
              ...defaultSection,
              ...savedSection,
              order: savedSection.order !== undefined ? savedSection.order : defaultSection.order,
            }
          }
          // New section not in saved settings, use default
          return defaultSection
        })
        // Add any extra sections from saved settings that aren't in defaults (for backwards compatibility)
        // But exclude combined sections
        const extraSections = filteredParsed.filter(
          (savedSection) => 
            !defaultSections.find((ds) => ds.id === savedSection.id) &&
            !combinedSectionIds.includes(savedSection.id),
        )
        const allSections = [...mergedSections, ...extraSections]
        // Re-sort by order
        allSections.sort((a, b) => a.order - b.order)
        setSections(allSections)
        if (onSettingsChange) {
          onSettingsChange(allSections)
        }
      } else {
        // Initialize with defaults if no saved settings
        const defaultSections = getDefaultSections()
        setSections(defaultSections)
        if (onSettingsChange) {
          onSettingsChange(defaultSections)
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error)
      // On error, use defaults
      const defaultSections = getDefaultSections()
      setSections(defaultSections)
      if (onSettingsChange) {
        onSettingsChange(defaultSections)
      }
    }
  }, [onSettingsChange, getDefaultSections])

  // Save settings to localStorage
  const saveSettings = useCallback(
    (newSections) => {
      try {
        // Remove any combined/legacy sections before saving
        const combinedSectionIds = [
          'complaints-observations-diagnosis',
          'complaints-observations-and-diagnosis',
          'complaints-observations-diagnosis-combined',
          'advice-notes', // Removed - split into 'advice' and 'free-form-report'
        ]
        const filteredSections = newSections.filter(
          (section) => !combinedSectionIds.includes(section.id),
        )
        
        localStorage.setItem(STORAGE_KEY, JSON.stringify(filteredSections))
        setSections(filteredSections)
        if (onSettingsChange) {
          onSettingsChange(filteredSections)
        }
      } catch (error) {
        console.error('Error saving settings:', error)
      }
    },
    [onSettingsChange],
  )

  // Helper to check if a section is supported (exists in DEFAULT_SECTIONS)
  const isSectionSupported = useCallback((sectionId) => {
    return DEFAULT_SECTIONS.some((defaultSection) => defaultSection.id === sectionId)
  }, [])

  // Filter out combined/legacy sections and sort by order
  const combinedSectionIds = [
    'complaints-observations-diagnosis',
    'complaints-observations-and-diagnosis',
    'complaints-observations-diagnosis-combined',
    'advice-notes', // Removed - split into 'advice' and 'free-form-report'
  ]
  const sortedSections = useMemo(() => {
    return [...sections]
      .filter((section) => !combinedSectionIds.includes(section.id))
      .sort((a, b) => a.order - b.order)
  }, [sections])

  // Toggle section visibility (only for supported sections)
  const toggleVisibility = useCallback(
    (sectionId) => {
      // Don't allow toggling unsupported sections
      if (!isSectionSupported(sectionId)) {
        return
      }
      const updated = sections.map((section) =>
        section.id === sectionId ? { ...section, visible: !section.visible } : section,
      )
      saveSettings(updated)
    },
    [sections, saveSettings, isSectionSupported],
  )

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Handle drag end for reordering
  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event
      if (!over || active.id === over.id) return

      const oldIndex = sortedSections.findIndex((section) => section.id === active.id)
      const newIndex = sortedSections.findIndex((section) => section.id === over.id)

      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        const items = arrayMove(sortedSections, oldIndex, newIndex)
        // Update order values
        const updated = items.map((item, index) => ({ ...item, order: index }))
        saveSettings(updated)
      }
    },
    [sortedSections, saveSettings],
  )

  // Reset to defaults
  const handleReset = useCallback(() => {
    if (window.confirm('Reset all settings to default?')) {
      saveSettings(DEFAULT_SECTIONS)
    }
  }, [saveSettings])

  // Fix aria-hidden accessibility issue: Remove aria-hidden from drawer root when drawer paper has focus
  useEffect(() => {
    if (!open) return

    const fixAriaHidden = () => {
      const drawerPaper = document.querySelector('[class*="MuiDrawer-paper"]')
      const drawerRoot = document.querySelector('[class*="MuiDrawer-root"][class*="MuiModal-root"]')
      
      if (drawerPaper && drawerRoot) {
        // Check if drawer paper or any of its children has focus
        const activeElement = document.activeElement
        if (drawerPaper.contains(activeElement)) {
          // Remove aria-hidden from drawer root when drawer has focus
          drawerRoot.removeAttribute('aria-hidden')
        }
      }
    }

    // Fix immediately and on focus events
    fixAriaHidden()
    
    // Listen for focus events within the drawer
    const handleFocus = () => {
      setTimeout(fixAriaHidden, 0)
    }
    
    document.addEventListener('focusin', handleFocus, true)
    
    return () => {
      document.removeEventListener('focusin', handleFocus, true)
    }
  }, [open])

  return (
    <>
      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={open}
        onClose={() => setOpen(false)}
        className="hide-on-print-only"
        ModalProps={{
          disableScrollLock: true,
          disableEnforceFocus: false,
          disableAutoFocus: false,
          hideBackdrop: false,
        }}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : '90%',
            maxWidth: isMobile ? '100%' : '500px',
            height: isMobile ? '95vh' : '100%',
            maxHeight: isMobile ? '95vh' : '100%',
            borderTopLeftRadius: isMobile ? '20px' : 0,
            borderTopRightRadius: isMobile ? '20px' : 0,
            borderBottomLeftRadius: isMobile ? 0 : 0,
            boxShadow: isMobile 
              ? '0 -4px 24px rgba(67, 56, 202, 0.15), 0 -2px 8px rgba(0, 0, 0, 0.1)'
              : '-4px 0 24px rgba(67, 56, 202, 0.15), -2px 0 8px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <DrawerContainer>
          <DrawerHeader>
            <DrawerTitle>
              <SettingsIcon />
              Editor Settings
            </DrawerTitle>
            <IconButton
              onClick={() => setOpen(false)}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DrawerHeader>
          
          <DrawerContent>
            <Box display="flex" flexDirection="column" gap={2} mb={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '16px' }}>
                  Section Visibility & Order
                </Typography>
                <Button size="small" onClick={handleReset} variant="outlined">
                  Reset Defaults
                </Button>
              </Box>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={sortedSections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                  <div>
                    {sortedSections.map((section) => {
                      const isSupported = isSectionSupported(section.id)
                      return (
                        <SortableSectionItem
                          key={section.id}
                          section={section}
                          isSupported={isSupported}
                          toggleVisibility={toggleVisibility}
                        />
                      )
                    })}
                  </div>
                </SortableContext>
              </DndContext>
            </Box>
          </DrawerContent>
        </DrawerContainer>
      </Drawer>
    </>
  )
}

export default TherapyReportSettings

